import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { postApiServices, getApiServices } from "../../apis/api";
import "../../styles/chatbot.css";
import EditIcon from "@mui/icons-material/Edit";
import FilePresentIcon from "@mui/icons-material/FilePresent";

const QUICK_QUESTIONS = [];
/* ── Local OCR API endpoints (Saravana's engine) ───────────── */
const LOCAL_OCR_URLS = [
  process.env.REACT_APP_OCR_API_URL ||  "http://router0.premierdis.com:8080",
];

/* ── OCR log parser to structured results ──────────────────── */
const parseLogsToOcrData = (data) => {
  if (!data) return data;
  if (!data.lines || !Array.isArray(data.lines)) {
    return data;
  }

  const results = [];
  let currentDoc = null;

  for (const line of data.lines) {
    const cleanLine = line.replace(/^\[\d{2}:\d{2}:\d{2}\]\s?/, "");
    
    // Match line like: "[1/3] image.png: 2/5 fields"
    const docMatch = cleanLine.match(/^\[(\d+)\/(\d+)\]\s+(.*):\s*(\d+)\/(\d+)\s+fields/);
    if (docMatch) {
      currentDoc = {
        fileName: docMatch[3],
        fields: {},
        data: {}
      };
      results.push(currentDoc);
      continue;
    }

    // Match field values line like: "  Merchant=Nasdaq & Amex, TOTAL AMOUNT=38.90"
    if (currentDoc && cleanLine.includes("=") && !cleanLine.includes("fields") && !cleanLine.includes("Done!") && !cleanLine.includes("JSON:")) {
      const parts = [];
      let currentPart = "";
      const rawParts = cleanLine.trim().split(",");
      
      for (let i = 0; i < rawParts.length; i++) {
        const p = rawParts[i];
        if (p.includes("=")) {
          if (currentPart) {
            parts.push(currentPart.trim());
          }
          currentPart = p;
        } else {
          currentPart += "," + p;
        }
      }
      if (currentPart) {
        parts.push(currentPart.trim());
      }

      for (const part of parts) {
        const eqIdx = part.indexOf("=");
        if (eqIdx !== -1) {
          const key = part.substring(0, eqIdx).trim();
          const val = part.substring(eqIdx + 1).trim();
          currentDoc.fields[key] = val;
          currentDoc.data[key] = { value: val, confidence: 1.0 };
        }
      }
    }
  }

  if (results.length > 0) {
    const mergedResults = (data.results && Array.isArray(data.results)) ? [...data.results] : [];
    
    for (const parsed of results) {
      const existingIdx = mergedResults.findIndex(r => r.fileName === parsed.fileName);
      if (existingIdx !== -1) {
        mergedResults[existingIdx] = {
          ...mergedResults[existingIdx],
          fields: { ...mergedResults[existingIdx].fields, ...parsed.fields },
          data: { ...mergedResults[existingIdx].data, ...parsed.data }
        };
      } else {
        mergedResults.push(parsed);
      }
    }
    
    return {
      ...data,
      results: mergedResults
    };
  }

  return data;
};

/* ── Truncate long filenames preserving extension ──────────── */
const truncateFilename = (name, maxLen = 28) => {
  if (!name || name.length <= maxLen) return name;
  const extIdx = name.lastIndexOf(".");
  if (extIdx !== -1 && name.length - extIdx <= 6) {
    const ext = name.substring(extIdx);
    const base = name.substring(0, extIdx);
    return base.substring(0, maxLen - ext.length - 3) + "..." + ext;
  }
  return name.substring(0, maxLen - 3) + "...";
};

/* ── OCR result parser ─────────────────────────────────────── */
const parseOCRScanResult = (rawData, targetFileName = null) => {
  if (!rawData) return "No OCR result received.";
  if (typeof rawData === "string") return rawData;
  
  const data = parseLogsToOcrData(rawData);
  
  if (data.results && Array.isArray(data.results) && data.results.length > 0) {
    const docResults = data.results.map((res, idx) => {
      if (targetFileName && res.fileName) {
        const name1 = res.fileName.toLowerCase();
        const name2 = targetFileName.toLowerCase();
        if (name1 !== name2 && !name1.includes(name2) && !name2.includes(name1)) {
          return "";
        }
      }
      const fields = res.fields || {};
      const fieldKeys = Object.keys(fields);
      const displayLabel = res.fileName ? truncateFilename(res.fileName) : "";
      const fileLabel = displayLabel ? `\n📄 **${displayLabel}**` : "";
      if (fieldKeys.length > 0) {
        const fieldLines = fieldKeys.map(k => `• **${k}**: ${fields[k]}`).join("\n");
        return `${fileLabel}\n${fieldLines}`;
      }
      return "";
    }).filter(Boolean).join("\n\n");
    
    if (docResults) {
      return `**OCR Scan Completed**\n\n**Detected Fields:**\n${docResults}`;
    }
  }

  if (data.text) return `**OCR Result:**\n${data.text}`;
  if (data.result) return `**OCR Result:**\n${typeof data.result === "string" ? data.result : JSON.stringify(data.result, null, 2)}`;
  if (data.fields && Array.isArray(data.fields)) {
    const fieldLines = data.fields.map(f => `• **${f.name || f.key || "Field"}**: ${f.value || f.text || ""}`).join("\n");
    return `**OCR Fields Detected:**\n${fieldLines}`;
  }
  // Fallback: stringify
  return `**OCR Result:**\n${JSON.stringify(data, null, 2)}`;
};

/* ── Fetch with custom timeout helper ──────────────────────── */
const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

/* ── Try fetching local OCR with queued flow ────────────────── */
const tryLocalOCR = async (file, baseUrl, timeoutMs = 90000) => {
  // 1. Upload files
  console.log(`[OCR] Uploading file to ${baseUrl}/api/upload-bulk...`);
  const formData = new FormData();
  formData.append("files", file);
  formData.append("file", file);

  const uploadRes = await fetchWithTimeout(`${baseUrl}/api/upload-bulk`, {
    method: "POST",
    body: formData,
  }, 10000);
  if (!uploadRes.ok) throw new Error(`Upload failed with status ${uploadRes.status}`);
  await uploadRes.json();

  // 2. Trigger run-extraction
  console.log(`[OCR] Triggering extraction at ${baseUrl}/api/run-extraction...`);
  const runRes = await fetchWithTimeout(`${baseUrl}/api/run-extraction`, {
    method: "POST",
  }, 10000);
  if (!runRes.ok) throw new Error(`Run extraction failed with status ${runRes.status}`);
  await runRes.json();

  // Wait a moment for backend to initialize extraction flags
  await new Promise(r => setTimeout(r, 1500));

  // 3. Poll status
  console.log(`[OCR] Polling status at ${baseUrl}/api/status...`);
  const startTime = Date.now();
  while (Date.now() - startTime < timeoutMs) {
    try {
      const statusRes = await fetchWithTimeout(`${baseUrl}/api/status?t=${Date.now()}`, {
        method: "GET",
      }, 15000);
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        const extractionRunning = statusData.extraction_running || statusData.extraction_running === "true" || statusData.prepare_running || statusData.prepare_running === "true";
        if (!extractionRunning) {
          console.log(`[OCR] Extraction finished.`);
          break;
        }
      }
    } catch (pollErr) {
      console.warn(`[OCR] Status poll request failed (will retry):`, pollErr.message);
    }
    await new Promise(r => setTimeout(r, 1500));
  }

  // 4. Fetch extracted results
  console.log(`[OCR] Fetching results from ${baseUrl}/api/logs/extract...`);
  const resultsRes = await fetchWithTimeout(`${baseUrl}/api/logs/extract`, {
    method: "GET",
  }, 10000);
  if (!resultsRes.ok) throw new Error(`Fetching results failed with status ${resultsRes.status}`);
  return await resultsRes.json();
};

/* ── Clean SVG Icons (Stroked / Lucide style) ──────────────── */

const ChatBubbleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const XIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SendArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const BotAvatarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8V4H8" />
    <rect width="16" height="12" x="4" y="8" rx="2" />
    <path d="M9 13h.01M15 13h.01M10 18h4" />
  </svg>
);

const UserAvatarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const NewChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-7" />
    <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const PaperclipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const renderMessageContent = (content) => {
  if (!content) return "";
  // Sanitize HTML characters to prevent XSS
  const escaped = content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  
  // Convert markdown bold (**text**) to HTML strong tags
  const formattedBold = escaped.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Convert markdown links ([text](url)) to HTML anchor tags
  const formatted = formattedBold.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="cb-link">$1</a>');
  
  // Convert bullet points starting with * or - at the start of a line
  const lines = formatted.split("\n").map((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("* ")) {
      return `• ${trimmed.substring(2)}`;
    }
    if (trimmed.startsWith("- ")) {
      return `• ${trimmed.substring(2)}`;
    }
    return line;
  });

  return (
    <span dangerouslySetInnerHTML={{ __html: lines.join("<br />") }} />
  );
};

export default function Chatbot() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [pendingTourUrl, setPendingTourUrl] = useState(null);
  const [attachedImage, setAttachedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [isSavingCorrection, setIsSavingCorrection] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // Scroll to bottom on message changes, loading updates, or open state
  useEffect(() => {
    if (isOpen) {
      scrollToBottom("smooth");
    }
  }, [messages, isLoading, isOpen, scrollToBottom]);

  // Delayed scroll on open to account for transition animations finishing
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        scrollToBottom("smooth");
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 400);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen && messages.length === 0) setHasUnread(true);
    }, 4000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleChat = useCallback(() => {
    if (isOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 280);
    } else {
      setIsOpen(true);
      setHasUnread(false);
    }
  }, [isOpen]);

  const clearAttachment = useCallback(() => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setAttachedImage(null);
    setImagePreview(null);
  }, [imagePreview]);

  const clearChat = useCallback(() => {
    setMessages([]);
    clearAttachment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAttachment]);

  const handleFileSelect = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setAttachedImage(file);
    setImagePreview(URL.createObjectURL(file));
  }, [imagePreview]);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) handleFileSelect(file);
        return;
      }
    }
  }, [handleFileSelect]);

  const handleTourDecision = useCallback(
    (accepted, tourUrl) => {
      if (accepted && tourUrl) {
        setMessages((prev) => [
          ...prev,
          { role: "user", content: "Yes" }
        ]);
        setPendingTourUrl(null);

        setIsLoading(true);
        setTimeout(() => {
          setIsLoading(false);
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: "Starting virtual tour..." }
          ]);

          const separator = tourUrl.includes("?") ? "&" : "?";
          const finalUrl = `${tourUrl}${separator}fromChatbot=true`;

          setTimeout(() => {
            setIsClosing(true);
            setTimeout(() => {
              setIsOpen(false);
              setIsClosing(false);
            }, 280);
            navigate(finalUrl);
          }, 800);
        }, 300);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "user", content: "No" },
          { role: "assistant", content: "Alright! Let me know if you need help with anything else." }
        ]);
        setPendingTourUrl(null);
      }
    },
    [navigate]
  );

  const sendMessage = useCallback(
    async (text) => {
      const msg = (text || input).trim();
      const hasImage = !!attachedImage;
      if (!msg && !hasImage) return;
      if (isLoading) return;

      // Build user message (with optional image)
      const userMsg = { role: "user", content: msg || "📷 Image sent for OCR" };
      if (hasImage) {
        userMsg.imageUrl = imagePreview;
        userMsg.imageFile = attachedImage;
      }
      setMessages((prev) => [...prev, userMsg]);
      setInput("");

      // Keep reference before clearing
      const imageFile = attachedImage;
      const previewUrl = imagePreview;
      setAttachedImage(null);
      setImagePreview(null);

      // Intercept if there is a pending tour decision
      if (pendingTourUrl && msg) {
        const lowerMsg = msg.toLowerCase();
        const isYes = ["yes", "y", "yeah", "yep", "sure", "ok", "yes please"].includes(lowerMsg);
        const isNo = ["no", "n", "nope", "nay", "no thanks"].includes(lowerMsg);

        if (isYes) {
          const tourUrl = pendingTourUrl;
          setPendingTourUrl(null);

          setIsLoading(true);
          setTimeout(() => {
            setIsLoading(false);
            setMessages((prev) => [
              ...prev,
              { role: "assistant", content: "Starting virtual tour..." }
            ]);

            const separator = tourUrl.includes("?") ? "&" : "?";
            const finalUrl = `${tourUrl}${separator}fromChatbot=true`;

            setTimeout(() => {
              setIsClosing(true);
              setTimeout(() => {
                setIsOpen(false);
                setIsClosing(false);
              }, 280);
              navigate(finalUrl);
            }, 800);
          }, 500);
          return;
        } else if (isNo) {
          setPendingTourUrl(null);
          setIsLoading(true);
          setTimeout(() => {
            setIsLoading(false);
            setMessages((prev) => [
              ...prev,
              { role: "assistant", content: "Alright! Let me know if you need help with anything else." }
            ]);
          }, 500);
          return;
        } else {
          setPendingTourUrl(null);
        }
      }

      setIsLoading(true);

      // ── OCR Image Flow ──────────────────────────────────────
      if (hasImage && imageFile) {
        try {
          let ocrData = null;

          // Attempt 1 & 2: Try local OCR endpoints directly from client
          for (const url of LOCAL_OCR_URLS) {
            try {
              console.log(`[OCR] Trying ${url}...`);
              ocrData = await tryLocalOCR(imageFile, url);
              console.log(`[OCR] Success from ${url}`);
              break;
            } catch (err) {
              console.warn(`[OCR] Failed ${url}:`, err.message);
            }
          }

          // Attempt 3: Fallback to server proxy using the queued flow
          if (!ocrData) {
            console.log("[OCR] Local calls failed, falling back to server proxy queued flow...");
            const reader = new FileReader();
            const base64 = await new Promise((resolve, reject) => {
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(imageFile);
            });

            // 1. Upload files via worker proxy
            console.log("[OCR Proxy] Uploading file...");
            const uploadRes = await postApiServices("ocr/upload-bulk", {
              imageBase64: base64,
              fileName: imageFile.name || "crop.png",
            });
            if (uploadRes?.status !== 200 && uploadRes?.data?.status !== "success") {
              throw new Error("Proxy upload failed");
            }

            // 2. Trigger extraction via worker proxy
            console.log("[OCR Proxy] Triggering extraction...");
            const runRes = await postApiServices("ocr/run-extraction", {});
            if (runRes?.status !== 200 && runRes?.data?.status !== "success") {
              throw new Error("Proxy trigger failed");
            }

            // Wait a moment for backend to initialize extraction flags
            await new Promise(r => setTimeout(r, 1500));

            // 3. Poll status via worker proxy
            console.log("[OCR Proxy] Polling status...");
            const startTime = Date.now();
            let isFinished = false;
            while (Date.now() - startTime < 120000) {
              try {
                const statusRes = await getApiServices(`ocr/status?t=${Date.now()}`);
                if (statusRes?.status === 200 || statusRes?.data?.status === "success") {
                  const statusData = statusRes?.data?.data || statusRes?.data;
                  const extractionRunning = statusData?.extraction_running || statusData?.extraction_running === "true" || statusData?.prepare_running || statusData?.prepare_running === "true";
                  if (!extractionRunning) {
                    isFinished = true;
                    break;
                  }
                }
              } catch (pollErr) {
                console.warn("[OCR Proxy] Status poll failed (will retry):", pollErr.message);
              }
              await new Promise(r => setTimeout(r, 1500));
            }

            if (!isFinished) throw new Error("Proxy polling timed out");

            // 4. Fetch results via worker proxy
            console.log("[OCR Proxy] Fetching results...");
            const resultsRes = await getApiServices("ocr/extract");
            if (resultsRes?.status === 200 || resultsRes?.data?.status === "success") {
              ocrData = resultsRes?.data?.data || resultsRes?.data;
            } else {
              throw new Error("Proxy fetch results failed");
            }
          }

          const parsedOcrData = parseLogsToOcrData(ocrData);
          const ocrText = parseOCRScanResult(parsedOcrData, imageFile.name);
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: ocrText, ocrData: parsedOcrData, uploadedFileName: imageFile.name },
          ]);
        } catch (err) {
          console.error("[OCR Flow Error]:", err);
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "I couldn't process the image for OCR. Please ensure the OCR service is running and try again.",
              isError: true,
            },
          ]);
        } finally {
          setIsLoading(false);
          // Clean up the object URL if we created one
          if (previewUrl) URL.revokeObjectURL(previewUrl);
        }
        return;
      }

      // ── Normal Chat Flow ────────────────────────────────────
      try {
        const history = messages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const response = await postApiServices("chat", {
          message: msg,
          history,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        let reply =
          response?.data?.data?.reply || response?.data?.reply || "Sorry, I couldn't process that request.";

        // Match and extract [REDIRECT:/url] tag
        const redirectRegex = /\[REDIRECT:(.*?)\]/;
        const match = reply.match(redirectRegex);
        let redirectUrl = null;

        if (match) {
          redirectUrl = match[1];
          reply = reply.replace(redirectRegex, "").trim();
        }

        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: reply },
        ]);

        if (redirectUrl) {
          const isAutoSearch = redirectUrl.includes("autoSubmit=true");
          if (isAutoSearch) {
            setMessages((prev) => [
              ...prev,
              { role: "assistant", content: "Taking you to search results..." },
            ]);
            setTimeout(() => {
              setIsClosing(true);
              setTimeout(() => {
                setIsOpen(false);
                setIsClosing(false);
              }, 280);
              navigate(redirectUrl);
            }, 1000);
          } else {
            setPendingTourUrl(redirectUrl);
            setMessages((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "Do you want to see a virtual tour for this feature?",
                isTourPrompt: true,
                tourUrl: redirectUrl,
              },
            ]);
          }
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "I'm having trouble connecting right now. Please try again in a moment.",
            isError: true,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [input, isLoading, messages, navigate, pendingTourUrl, attachedImage, imagePreview]
  );

  const saveFieldCorrection = useCallback(
    async (msgIndex, resIdx, fieldName) => {
      if (!editingField) return;
      const correctedValue = editingField.value;
      const msg = messages[msgIndex];
      const ocrData = msg?.ocrData;
      const result = ocrData?.results?.[resIdx];
      const fieldsData = result?.data || {};
      const fieldMeta = fieldsData[fieldName] || {};

      const payload = {
        index: resIdx,
        field_name: fieldName,
        corrected_value: correctedValue,
        bbox: fieldMeta.bbox || [],
        original_value: fieldMeta.value || result?.fields?.[fieldName] || "",
        is_new_field: false
      };

      setIsSavingCorrection(true);
      try {
        console.log("[OCR Correction] Saving correction via server proxy...");
        const res = await postApiServices("desktop-ocr-correction", payload);
        const success = res?.status === 200 || res?.data?.status === "success";

        if (success) {
          setMessages((prev) => {
            const next = [...prev];
            const targetMsg = { ...next[msgIndex] };
            const updatedOcrData = { ...targetMsg.ocrData };
            const updatedResults = [...updatedOcrData.results];
            const updatedRes = { ...updatedResults[resIdx] };
            updatedRes.fields = {
              ...updatedRes.fields,
              [fieldName]: correctedValue
            };
            if (updatedRes.data && updatedRes.data[fieldName]) {
              updatedRes.data = {
                ...updatedRes.data,
                [fieldName]: {
                  ...updatedRes.data[fieldName],
                  value: correctedValue
                }
              };
            }
            updatedResults[resIdx] = updatedRes;
            updatedOcrData.results = updatedResults;
            targetMsg.ocrData = updatedOcrData;
            targetMsg.content = parseOCRScanResult(updatedOcrData, targetMsg.uploadedFileName);
            next[msgIndex] = targetMsg;
            return next;
          });

          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: `Successfully updated field **${fieldName}** to "${correctedValue}".` }
          ]);
          setEditingField(null);
        } else {
          throw new Error("Could not save correction.");
        }
      } catch (err) {
        console.error(err);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Failed to save correction for field **${fieldName}**. Please ensure the OCR service is reachable.`, isError: true }
        ]);
      } finally {
        setIsSavingCorrection(false);
      }
    },
    [editingField, messages]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return createPortal(
    <>
      {/* ── Floating Action Button ──────────────────── */}
      <button
        id="tour-chatbot"
        className={`cb-fab ${isOpen ? "cb-fab--active" : ""}`}
        onClick={toggleChat}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        <span className="cb-fab__icon cb-fab__icon--chat">
          <ChatBubbleIcon />
        </span>
        <span className="cb-fab__icon cb-fab__icon--close">
          <XIcon />
        </span>
        {hasUnread && !isOpen && <span className="cb-fab__badge" />}
      </button>

      {/* ── Chat Window ─────────────────────────────── */}
      {(isOpen || isClosing) && (
        <div
          id="chatbot-window"
          className={`cb-window ${isClosing ? "cb-window--closing" : ""}`}
        >
          {/* Header */}
          <div className="cb-header">
            <div className="cb-header__left">
              <div className="cb-header__avatar">
                <SparkleIcon />
              </div>
              <div className="cb-header__text">
                <h3 className="cb-header__title">PD Assistant</h3>
                <span className="cb-header__status">
                  <span className="cb-header__dot" />
                  Always online
                </span>
              </div>
            </div>

            <div className="cb-header__actions">
              {messages.length > 0 && (
                <button
                  className="cb-header__new-chat"
                  onClick={clearChat}
                  aria-label="New chat"
                  title="New chat"
                >
                  <NewChatIcon />
                </button>
              )}
              <button
                className="cb-header__close"
                onClick={toggleChat}
                aria-label="Close"
              >
                <XIcon />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="cb-body">
            {/* Welcome Card */}
            {messages.length === 0 && !isLoading && (
              <div className="cb-welcome">
                <div className="cb-welcome__icon">
                  <SparkleIcon />
                </div>
                <h4 className="cb-welcome__title">
                  Hi there! How can I help?
                </h4>
                <p className="cb-welcome__desc">
                  I can assist you with documents, search, categories, users,
                  and more.
                </p>
                <div className="cb-welcome__chips">
                  {QUICK_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      className="cb-chip"
                      onClick={() => sendMessage(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages List */}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`cb-msg ${m.role === "user" ? "cb-msg--user" : "cb-msg--ai"}`}
              >
                {m.role !== "user" && (
                  <div className="cb-msg__avatar cb-msg__avatar--ai">
                    <BotAvatarIcon />
                  </div>
                )}
                <div
                  className={`cb-msg__bubble ${m.isError ? "cb-msg__bubble--error" : ""} ${m.isTourPrompt ? "cb-msg__bubble--tour-prompt" : ""}`}
                >
                  {m.imageUrl && (
                    <img
                      src={m.imageUrl}
                      alt="Attached"
                      className="cb-msg__image"
                    />
                  )}
                  {renderMessageContent(m.content)}
                  {m.ocrData && m.ocrData.results && Array.isArray(m.ocrData.results) && m.ocrData.results.length > 0 && (
                    <div className="cb-ocr-fields">
                      <div className="cb-ocr-title">Detected Fields & Corrections:</div>
                      {m.ocrData.results.map((result, resIdx) => {
                        const fields = result.fields || {};
                        const fieldsData = result.data || {};
                        const fileLabel = result.fileName ? truncateFilename(result.fileName) : `Document ${resIdx + 1}`;
                        if (Object.keys(fields).length === 0) return null;

                        if (m.uploadedFileName && result.fileName) {
                          const name1 = result.fileName.toLowerCase();
                          const name2 = m.uploadedFileName.toLowerCase();
                          if (name1 !== name2 && !name1.includes(name2) && !name2.includes(name1)) {
                            return null;
                          }
                        }

                        return (
                          <div key={resIdx} className="cb-ocr-doc-section" style={{ marginBottom: "12px", borderBottom: resIdx < m.ocrData.results.length - 1 ? "1px dashed rgba(255,255,255,0.15)" : "none", paddingBottom: "10px" }}>
                            <div className="cb-ocr-doc-filename">
                              <FilePresentIcon style={{ fontSize: 16, color: "#64748b" }} /> {fileLabel}
                            </div>
                             <div className="cb-ocr-table">
                                {Object.keys(fields).map((fieldName) => {
                                  const isEditing = editingField && editingField.msgIndex === i && editingField.resIdx === resIdx && editingField.fieldName === fieldName;
                                  const fieldValue = fields[fieldName];
                                  const fieldMeta = fieldsData[fieldName] || {};
                                  const confidence = fieldMeta.confidence !== undefined ? Math.round(fieldMeta.confidence * 100) : null;

                                  return (
                                    <div key={fieldName} className={`cb-ocr-row ${isEditing ? "cb-ocr-row--editing" : ""}`}>
                                      <div className="cb-ocr-label">
                                        <strong>{fieldName}</strong>
                                        {confidence !== null && (
                                          <span className="cb-ocr-conf" title={`Confidence: ${confidence}%`}>
                                            {confidence}%
                                          </span>
                                        )}
                                      </div>
                                      <div className="cb-ocr-value">
                                        {isEditing ? (
                                          <div className="cb-ocr-edit-group">
                                            <input
                                              type="text"
                                              className="cb-ocr-input"
                                              value={editingField.value}
                                              onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                                              disabled={isSavingCorrection}
                                              autoFocus
                                            />
                                            <button
                                              className="cb-ocr-btn cb-ocr-btn--save"
                                              onClick={() => saveFieldCorrection(i, resIdx, fieldName)}
                                              disabled={isSavingCorrection}
                                              title="Save Correction"
                                            >
                                              ✓
                                            </button>
                                            <button
                                              className="cb-ocr-btn cb-ocr-btn--cancel"
                                              onClick={() => setEditingField(null)}
                                              disabled={isSavingCorrection}
                                              title="Cancel"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="cb-ocr-display-group">
                                            <span className="cb-ocr-val-text">{fieldValue}</span>
                                            <button
                                              className="cb-ocr-edit-trigger"
                                              onClick={() => setEditingField({ msgIndex: i, resIdx, fieldName, value: fieldValue })}
                                              title="Correct this field"
                                            >
                                              <EditIcon style={{ fontSize: 14 }} /> 
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                             </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {m.isTourPrompt && pendingTourUrl === m.tourUrl && (
                    <div className="cb-tour-buttons">
                      <button
                        className="cb-tour-btn cb-tour-btn--yes"
                        onClick={() => handleTourDecision(true, m.tourUrl)}
                      >
                        Yes
                      </button>
                      <button
                        className="cb-tour-btn cb-tour-btn--no"
                        onClick={() => handleTourDecision(false)}
                      >
                        No
                      </button>
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="cb-msg__avatar cb-msg__avatar--user">
                    <UserAvatarIcon />
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="cb-msg cb-msg--ai">
                <div className="cb-msg__avatar cb-msg__avatar--ai">
                  <BotAvatarIcon />
                </div>
                <div className="cb-msg__bubble cb-msg__bubble--typing">
                  <span className="cb-dot" />
                  <span className="cb-dot" />
                  <span className="cb-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Bar */}
          <div className="cb-footer">
            {/* Image preview strip */}
            {imagePreview && (
              <div className="cb-footer__preview">
                <img src={imagePreview} alt="Preview" className="cb-footer__preview-img" />
                <button
                  className="cb-footer__preview-remove"
                  onClick={clearAttachment}
                  aria-label="Remove image"
                >
                  ×
                </button>
              </div>
            )}
            <div className="cb-footer__field">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                  e.target.value = "";
                }}
              />
              <button
                id="chatbot-attach"
                className="cb-footer__attach"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                aria-label="Attach image"
                title="Attach image for OCR"
              >
                <PaperclipIcon />
              </button>
              <input
                ref={inputRef}
                id="chatbot-input"
                className="cb-footer__input"
                type="text"
                placeholder={attachedImage ? "Add a note (optional)…" : "Type your message…"}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={isLoading}
                autoComplete="off"
              />
              <button
                id="chatbot-send"
                className="cb-footer__send"
                onClick={() => sendMessage()}
                disabled={isLoading || (!input.trim() && !attachedImage)}
                aria-label="Send"
              >
                <SendArrowIcon />
              </button>
            </div>
            <span className="cb-footer__hint">
              Powered by AI · Attach images for OCR
            </span>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

